import { Report } from '@nx-smartmonkey/shared/domain';
import { getColorByIndex, RoutalPalette } from '@nx-smartmonkey/ui';

import { Driver } from '../domain/driver/Driver';
import { DriverStatus } from '../domain/driver/DriverStatus';
import { PlanStatus } from '../domain/plan/PlanStatus';
import { StopStatus } from '../domain/stop/StopStatus';
import { TaskStatus } from '../domain/task/TaskStatus';
import { Vehicle } from '../domain/vehicle/Vehicle';
import { VehicleFlat } from '../domain/vehicle/VehicleFlat';
import DRIVER_STATUS from './Drivers';

const getColor = (
  elementData: Driver | Vehicle | VehicleFlat,
  elements: Array<Driver> | Array<Vehicle> | Array<VehicleFlat>
) => {
  if (!elementData) {
    return getColorByIndex(0);
  }

  if (elementData.color) {
    return elementData.color.value;
  } else {
    const routeIndex = elements.findIndex((r: any) => r.id === elementData.id);
    return getColorByIndex(routeIndex);
  }
};

export const getRouteColor = (routeData: Driver, planDrivers: Driver[]) => getColor(routeData, planDrivers);

export const getVehicleColor = (vehicleData: Vehicle | VehicleFlat, vehicles: Array<Vehicle> | Array<VehicleFlat>) =>
  getColor(vehicleData, vehicles);

export const getPlanStatusColor = (status: PlanStatus, type?: `light` | `medium` | `dark`) => {
  switch (status) {
    case `finished`:
      return RoutalPalette.planStatusColors.finished[type ?? `medium`];
    case `planning`:
      return RoutalPalette.planStatusColors.planning[type ?? `medium`];
    case `in_progress`:
      return RoutalPalette.planStatusColors.in_progress[type ?? `medium`];
    default:
      return RoutalPalette.planStatusColors.draft[type ?? `medium`];
  }
};

export const getStopBadgeStatusColor = (status: StopStatus, type?: `light` | `medium` | `dark`) => {
  switch (status) {
    case `completed`:
      return RoutalPalette.stopStatusColors.completed[type ?? `medium`];
    case `incomplete`:
      return RoutalPalette.stopStatusColors.incomplete[type ?? `medium`];
    case `canceled`:
      return RoutalPalette.stopStatusColors.canceled[type ?? `medium`];
    default:
      return RoutalPalette.stopStatusColors.pending[type ?? `medium`];
  }
};

export const getStopStatusColor = (status: StopStatus, type?: `light` | `medium` | `dark`) => {
  switch (status) {
    case `completed`:
      return RoutalPalette.markerStopColors.completed[type ?? `medium`];
    case `incomplete`:
      return RoutalPalette.markerStopColors.incomplete[type ?? `medium`];
    case `canceled`:
      return RoutalPalette.markerStopColors.canceled[type ?? `medium`];
    default:
      return RoutalPalette.stopStatusColors.pending[type ?? `medium`];
  }
};

export const getTaskStatusColor = (status: TaskStatus, type?: `light` | `medium` | `dark`) => {
  switch (status) {
    case `completed`:
      return RoutalPalette.stopStatusColors.completed[type ?? `medium`];
    case `canceled`:
      return RoutalPalette.stopStatusColors.canceled[type ?? `medium`];
    default:
      return RoutalPalette.stopStatusColors.pending[type ?? `medium`];
  }
};

export const getReportStatusColor = (report: Report) => {
  let color;
  switch (report.type) {
    case `service_report_completed`:
      color = getStopStatusColor(`completed`);
      if ((report.tasks ?? []).length > 0) {
        if (report.tasks!.some((task) => task.status !== `completed`)) {
          color = getStopStatusColor(`incomplete`);
        }
      }
      break;
    case `service_report_canceled`:
      color = getStopStatusColor(`canceled`);
      break;
  }
  return color;
};

export const getDriverStatusColor = (driverStatus: DriverStatus, type?: `light` | `medium` | `dark`) => {
  if (driverStatus === DRIVER_STATUS.notStarted) {
    return RoutalPalette.routeStatusColors.pending[type ?? `medium`];
  }
  if (driverStatus === DRIVER_STATUS.inTransit) {
    return RoutalPalette.routeStatusColors.in_transit[type ?? `medium`];
  }
  return RoutalPalette.routeStatusColors.finished[type ?? `medium`];
};
